import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  Transaction,
  Payout,
  Invoice,
  Receipt,
  ListResult,
  TxFilter,
  PayoutFilter,
  InvoiceFilter,
} from "./types";
import { presetToFromTimestamp } from "./types";

const client = generateClient<Schema>();

// ── Secondary-index cast helpers ──────────────────────────────────────────────
// Amplify Gen 2 generates listBy* methods from secondary indexes at runtime but
// does not expose them in the generated TypeScript types. Access via narrowly-
// scoped cast, mirroring the pattern in events_repository.ts.

type ListByResult<T> = Promise<{
  data: T[];
  nextToken?: string | null;
  errors?: { message: string }[];
}>;

interface ListByOpts {
  sortDirection?: "ASC" | "DESC";
  limit?: number;
  nextToken?: string;
  filter?: Record<string, unknown>;
}

type CreatedCondition =
  { ge: number } | { le: number } | { between: [number, number] };

interface ByBusinessIdAndCreatedKey {
  businessId: string;
  created?: CreatedCondition;
}

function listTransactionByBusinessIdAndCreated(
  key: ByBusinessIdAndCreatedKey,
  opts: ListByOpts,
): ListByResult<Transaction> {
  const model = client.models.Transaction as unknown as {
    listTransactionByBusinessIdAndCreated: (
      key: ByBusinessIdAndCreatedKey,
      opts: ListByOpts,
    ) => ListByResult<Transaction>;
  };
  return model.listTransactionByBusinessIdAndCreated(key, opts);
}

function listPayoutByBusinessIdAndCreated(
  key: ByBusinessIdAndCreatedKey,
  opts: ListByOpts,
): ListByResult<Payout> {
  const model = client.models.Payout as unknown as {
    listPayoutByBusinessIdAndCreated: (
      key: ByBusinessIdAndCreatedKey,
      opts: ListByOpts,
    ) => ListByResult<Payout>;
  };
  return model.listPayoutByBusinessIdAndCreated(key, opts);
}

function listInvoiceByBusinessIdAndCreated(
  key: ByBusinessIdAndCreatedKey,
  opts: ListByOpts,
): ListByResult<Invoice> {
  const model = client.models.Invoice as unknown as {
    listInvoiceByBusinessIdAndCreated: (
      key: ByBusinessIdAndCreatedKey,
      opts: ListByOpts,
    ) => ListByResult<Invoice>;
  };
  return model.listInvoiceByBusinessIdAndCreated(key, opts);
}

function listReceiptByTransactionId(
  transactionId: string,
): ListByResult<Receipt> {
  const model = client.models.Receipt as unknown as {
    listReceiptByTransactionId: (key: {
      transactionId: string;
    }) => ListByResult<Receipt>;
  };
  return model.listReceiptByTransactionId({ transactionId });
}

// ── Filter builders ───────────────────────────────────────────────────────────

/**
 * Builds the GSI sort-key condition on `created` for the
 * businessId.created index. `created` is a primary key attribute on the GSI
 * and must be passed as a sort-key condition on the index query — it cannot
 * appear in the filter expression (DynamoDB rejects that with
 * `Filter Expression can only contain non-primary key attributes`).
 */
function buildCreatedCondition(
  dateRange:
    | TxFilter["dateRange"]
    | PayoutFilter["dateRange"]
    | InvoiceFilter["dateRange"],
): CreatedCondition | undefined {
  if (dateRange.preset === "all") return undefined;

  if (dateRange.preset !== "custom") {
    return { ge: presetToFromTimestamp(dateRange.preset) };
  }

  return { between: [dateRange.range.from, dateRange.range.to] };
}

function buildStatusFilter<S extends string>(
  statuses: S[],
): Record<string, unknown> | undefined {
  if (statuses.length === 0) return undefined;
  if (statuses.length === 1) return { status: { eq: statuses[0] } };
  return { or: statuses.map((s) => ({ status: { eq: s } })) };
}

/**
 * Builds the DynamoDB `filter` expression for the non-key transaction filters
 * (status, text search, refund state, dispute state). The `created` range stays
 * on the index key condition (see buildCreatedCondition).
 */
function buildTransactionFilter(
  filter: TxFilter,
): Record<string, unknown> | undefined {
  const and: Record<string, unknown>[] = [];

  const status = buildStatusFilter(filter.statuses);
  if (status) and.push(status);

  if (filter.searchQuery.trim()) {
    const q = filter.searchQuery.trim();
    and.push({
      or: [{ sourceChargeId: { contains: q } }, { cposPayId: { contains: q } }],
    });
  }

  if (filter.refundState === "refunded")
    and.push({ refundedAmount: { gt: 0 } });
  else if (filter.refundState === "not_refunded")
    and.push({
      or: [
        { refundedAmount: { attributeExists: false } },
        { refundedAmount: { eq: 0 } },
      ],
    });
  else if (filter.refundState === "partial_only")
    // partial = refunded > 0 AND refunded < amount; DynamoDB can't compare two
    // attributes, so the bloc narrows partial_only on the current page (see transactions_list_bloc).
    and.push({ refundedAmount: { gt: 0 } });

  if (filter.disputeState === "disputed")
    and.push({ dispute: { attributeExists: true } });
  else if (filter.disputeState === "not_disputed")
    and.push({ dispute: { attributeExists: false } });

  if (and.length === 0) return undefined;
  if (and.length === 1) return and[0];
  return { and };
}

/**
 * Builds the DynamoDB `filter` expression for invoice list queries. Mirrors
 * buildTransactionFilter: status filter plus a substring search over the same
 * fields the former client-side filter narrowed on (description, id). The
 * `created` range stays on the index key condition (see buildCreatedCondition).
 */
function buildInvoiceFilter(
  filter: InvoiceFilter,
): Record<string, unknown> | undefined {
  const and: Record<string, unknown>[] = [];

  const status = buildStatusFilter(filter.statuses);
  if (status) and.push(status);

  if (filter.searchQuery.trim()) {
    const q = filter.searchQuery.trim();
    and.push({
      or: [{ description: { contains: q } }, { id: { contains: q } }],
    });
  }

  if (and.length === 0) return undefined;
  if (and.length === 1) return and[0];
  return { and };
}

// ── Repository ────────────────────────────────────────────────────────────────

export class MoneyRepository {
  async listTransactions(opts: {
    businessId: string;
    limit: number;
    nextToken?: string | null;
    filter?: TxFilter;
  }): Promise<ListResult<Transaction>> {
    const created = opts.filter
      ? buildCreatedCondition(opts.filter.dateRange)
      : undefined;
    const amplifyFilter = opts.filter
      ? buildTransactionFilter(opts.filter)
      : undefined;

    const { data, nextToken, errors } =
      await listTransactionByBusinessIdAndCreated(
        { businessId: opts.businessId, ...(created && { created }) },
        {
          sortDirection: "DESC",
          limit: opts.limit,
          nextToken: opts.nextToken ?? undefined,
          filter: amplifyFilter,
        },
      );

    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: data ?? [],
      nextToken: nextToken ?? null,
    };
  }

  async listPayouts(opts: {
    businessId: string;
    limit: number;
    nextToken?: string | null;
    filter?: PayoutFilter;
  }): Promise<ListResult<Payout>> {
    const created = opts.filter
      ? buildCreatedCondition(opts.filter.dateRange)
      : undefined;
    const amplifyFilter = opts.filter
      ? buildStatusFilter(opts.filter.statuses)
      : undefined;

    const { data, nextToken, errors } = await listPayoutByBusinessIdAndCreated(
      { businessId: opts.businessId, ...(created && { created }) },
      {
        sortDirection: "DESC",
        limit: opts.limit,
        nextToken: opts.nextToken ?? undefined,
        filter: amplifyFilter,
      },
    );

    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: data ?? [],
      nextToken: nextToken ?? null,
    };
  }

  async listInvoices(opts: {
    businessId: string;
    limit: number;
    nextToken?: string | null;
    filter?: InvoiceFilter;
  }): Promise<ListResult<Invoice>> {
    const created = opts.filter
      ? buildCreatedCondition(opts.filter.dateRange)
      : undefined;
    const amplifyFilter = opts.filter
      ? buildInvoiceFilter(opts.filter)
      : undefined;

    const { data, nextToken, errors } = await listInvoiceByBusinessIdAndCreated(
      { businessId: opts.businessId, ...(created && { created }) },
      {
        sortDirection: "DESC",
        limit: opts.limit,
        nextToken: opts.nextToken ?? undefined,
        filter: amplifyFilter,
      },
    );

    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: data ?? [],
      nextToken: nextToken ?? null,
    };
  }

  /**
   * Batched lookup of transactions by invoiceId. Used for the "Paid via" column
   * on the Invoices list. Returns a map of invoiceId → Transaction.
   * Uses .list() with invoiceId filter (post-fetch scan), paginating until all
   * matching transactions are found or all invoiceIds are accounted for.
   */
  async batchLookupTransactionsForInvoices(
    invoiceIds: string[],
  ): Promise<Map<string, Transaction>> {
    if (invoiceIds.length === 0) return new Map();

    const result = new Map<string, Transaction>();
    const targetCount = invoiceIds.length;
    let nextToken: string | undefined;

    do {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
      const listOpts: any = {
        filter: { or: invoiceIds.map((id) => ({ invoiceId: { eq: id } })) },
        limit: 500,
        ...(nextToken && { nextToken }),
      };

      const {
        data,
        errors,
        nextToken: pageToken,
      } = await client.models.Transaction.list(listOpts);
      if (errors?.length) throw new Error(errors[0].message);

      for (const tx of data ?? []) {
        if (tx.invoiceId) {
          result.set(tx.invoiceId, tx);
        }
      }

      nextToken = pageToken ?? undefined;
    } while (nextToken && result.size < targetCount);

    return result;
  }

  /** Drains every page of a cursor-paginated list method into a single array. */
  private async _collectAll<T>(
    fetchPage: (nextToken?: string | null) => Promise<ListResult<T>>,
  ): Promise<T[]> {
    const allItems: T[] = [];
    let cursor: string | undefined;

    do {
      const result = await fetchPage(cursor);
      allItems.push(...result.items);
      cursor = result.nextToken ?? undefined;
    } while (cursor);

    return allItems;
  }

  async listAllTransactions(opts: {
    businessId: string;
    filter?: TxFilter;
  }): Promise<Transaction[]> {
    return this._collectAll((nextToken) =>
      this.listTransactions({
        businessId: opts.businessId,
        limit: 500,
        nextToken,
        filter: opts.filter,
      }),
    );
  }

  async listAllPayouts(opts: {
    businessId: string;
    filter?: PayoutFilter;
  }): Promise<Payout[]> {
    return this._collectAll((nextToken) =>
      this.listPayouts({
        businessId: opts.businessId,
        limit: 500,
        nextToken,
        filter: opts.filter,
      }),
    );
  }

  async listAllInvoices(opts: {
    businessId: string;
    filter?: InvoiceFilter;
  }): Promise<Invoice[]> {
    return this._collectAll((nextToken) =>
      this.listInvoices({
        businessId: opts.businessId,
        limit: 500,
        nextToken,
        filter: opts.filter,
      }),
    );
  }

  async getTransaction(id: string): Promise<Transaction | null> {
    const { data, errors } = await client.models.Transaction.get({ id });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? null;
  }

  async listReceiptsForTransaction(transactionId: string): Promise<Receipt[]> {
    const { data, errors } = await listReceiptByTransactionId(transactionId);
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? [];
  }

  async getInvoice(invoiceId: string): Promise<Invoice | null> {
    const { data, errors } = await client.models.Invoice.get({ id: invoiceId });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? null;
  }
}
